import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, errMsg } from "@/lib/api";
import { formatAppointmentServices } from "@/lib/appointment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, CheckCircle2, PenLine } from "lucide-react";
import { applyBrandColor } from "@/lib/theme";

// Standalone signature pad tailored for the mobile remote-signature flow.
// Kept local to this page so the file works without touching ChecklistPage.
function SignaturePad({ value, onChange, disabled }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const w = c.offsetWidth;
    const h = 220;
    c.width = w * dpr;
    c.height = h * dpr;
    c.style.height = `${h}px`;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#111";
  }, []);
  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };
  const start = (e) => { if (disabled) return; e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (disabled || !drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (disabled || !drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    if (disabled) return;
    const c = canvasRef.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    onChange("");
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Assine no espaço abaixo</Label>
        <Button type="button" size="sm" variant="outline" onClick={clear} disabled={disabled} data-testid="btn-clear-sign">Limpar</Button>
      </div>
      <canvas
        ref={canvasRef}
        data-testid="remote-signature-pad"
        className={`w-full rounded-lg border-2 border-dashed border-border bg-white touch-none ${disabled ? "pointer-events-none opacity-70" : ""}`}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <p className="text-xs text-muted-foreground">Use o dedo ou stylus para desenhar sua assinatura.</p>
    </div>
  );
}

export default function ChecklistSignPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [signature, setSignature] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/checklist/sign/${token}`);
        setCtx(data);
        setName(data.appointment?.client_name || "");
        setAlreadySigned(!!data.already_signed);
        if (data.tenant?.brand_color) applyBrandColor(data.tenant.brand_color);
      } catch (err) {
        setError(errMsg(err, "Link inválido ou expirado"));
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async () => {
    if (!signature) { toast.error("Por favor, assine antes de enviar."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/public/checklist/sign/${token}`, { signature, client_name: name });
      setDone(true);
    } catch (err) { toast.error(errMsg(err, "Falha ao enviar assinatura")); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground"><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Carregando…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="surface p-6 max-w-md text-center space-y-2">
          <h1 className="font-display text-xl font-bold">Link indisponível</h1>
          <p className="text-sm text-muted-foreground" data-testid="sign-error">{error}</p>
          <p className="text-xs text-muted-foreground">Peça um novo link ao estabelecimento.</p>
        </div>
      </div>
    );
  }
  const appt = ctx?.appointment || {};
  const tenant = ctx?.tenant || {};
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-lg mx-auto p-4 sm:p-6 space-y-4">
        <div className="surface p-5 space-y-2 text-center">
          {tenant.logo_url && <img src={tenant.logo_url} alt={tenant.name} className="h-12 mx-auto object-contain" />}
          <h1 className="font-display text-2xl font-bold">{tenant.name || "Assinatura"}</h1>
          <p className="text-sm text-muted-foreground">Confirmação do check-list de veículo</p>
        </div>

        <div className="surface p-5 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Serviço</span><span className="font-medium text-right">{formatAppointmentServices(appt) || "—"}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Profissional</span><span className="font-medium">{appt.professional_name || "—"}</span></div>
          {appt.scheduled_at && (
            <div className="flex justify-between"><span className="text-muted-foreground">Data</span><span className="font-medium">{new Date(appt.scheduled_at).toLocaleString("pt-BR")}</span></div>
          )}
        </div>

        {done || alreadySigned ? (
          <div className="surface p-6 space-y-3 text-center" data-testid="sign-done">
            <CheckCircle2 className="h-12 w-12 mx-auto text-green-600" />
            <h2 className="font-semibold text-lg">Assinatura registrada</h2>
            <p className="text-sm text-muted-foreground">
              Obrigado{name ? `, ${name}` : ""}! Sua confirmação foi enviada ao estabelecimento.
            </p>
          </div>
        ) : (
          <div className="surface p-5 space-y-4">
            <div className="space-y-1">
              <Label>Seu nome</Label>
              <Input
                data-testid="input-remote-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Como deseja assinar"
                disabled={saving}
              />
            </div>
            <SignaturePad value={signature} onChange={setSignature} disabled={saving} />
            <Button
              className="w-full h-12"
              onClick={submit}
              disabled={saving || !signature}
              data-testid="btn-submit-signature"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PenLine className="h-4 w-4 mr-2" />}
              Enviar assinatura
            </Button>
            <p className="text-[11px] text-muted-foreground text-center">
              Ao assinar você confirma o recebimento e as condições do veículo descritas no check-list.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
